import { useMutation, useQuery } from "@apollo/client";
import { IonCol, IonGrid, IonRow } from "@ionic/react";
import { utcToZonedTime } from "date-fns-tz";
import { useCallback, useMemo, useState } from "react";
import { FormattedDate, FormattedMessage, useIntl } from "react-intl";

import { sortByTimestamp } from "@/utils/modelUtils";
import ActionIconButton from "@components/buttons/ActionIconButton";
import PrimaryButton from "@components/buttons/PrimaryButton";
import SecondaryButton from "@components/buttons/SecondaryButton";
import DateAndTimeStamp from "@components/dates/DateAndTimeStamp";
import DateStamp from "@components/dates/DateStamp";
import ExpertAvatar from "@components/ExpertAvatar";
import HandleQuery from "@components/HandleQuery";
import Modal from "@components/Modal";
import styles from "@components/OutOfOfficeCalendar.module.css";
import TextWithIcon from "@components/text/TextWithIcon";
import useMe from "@hooks/useMe";
import useMyAdmin from "@hooks/useMyAdmin";
import useMyExpert from "@hooks/useMyExpert";
import useNodesFromConnection from "@hooks/useNodesFromConnection";
import useOpener from "@hooks/useOpener";
import { oneDayInMs } from "@typing/Constants";
import {
  ExpertGlobalRoleEnum,
  GetGrayceHolidaysDocument,
  GetGrayceHolidaysQuery,
  GetGrayceHolidaysQueryVariables,
  GetOutOfOfficePeriodsDocument,
  GetOutOfOfficePeriodsQuery,
  GetOutOfOfficePeriodsQueryVariables,
  OutOfOfficePeriodDestroyDocument,
  OutOfOfficePeriodDestroyMutation,
  OutOfOfficePeriodDestroyMutationVariables
} from "@typing/Generated";
import { getWeekdays } from "@utils/timeUtils";

type OutOfOfficePeriod = NonNullable<
  NonNullable<NonNullable<GetOutOfOfficePeriodsQuery["outOfOfficePeriods"]["edges"]>[number]>["node"]
>;

type Props = {
  onEdit: (ooop: OutOfOfficePeriod) => void;
};

enum DayOoopPhase {
  COMPLETE = "COMPLETE",
  CONTINUE = "CONTINUE",
  END = "END",
  START = "START",
  START_END = "START_END"
}

type DayOoopType = {
  outOfOfficePeriod: OutOfOfficePeriod;
  phase: DayOoopPhase;
};

type Day = {
  date: Date;
  holiday: boolean;
  id: number;
  ooops: DayOoopType[];
};

type Week = {
  days: Day[];
  id: number;
};

type OoopDetailProps = {
  onCancel: () => void;
  onEdit: (outOfOfficePeriod: OutOfOfficePeriod) => void;
  outOfOfficePeriod: OutOfOfficePeriod;
};

type DayOoopProps = {
  onClick: (outOfOfficePeriod: OutOfOfficePeriod) => void;
  outOfOfficePeriod: OutOfOfficePeriod;
  phase: DayOoopPhase;
};

const DayOoop = ({ onClick, outOfOfficePeriod, phase }: DayOoopProps) => {
  const renderExpertData = useCallback(
    () => (
      <div className={styles.expertData}>
        <div>
          <ExpertAvatar className={styles.expertPhoto} expert={outOfOfficePeriod.expert} size={50} />
        </div>
        <div className={styles.expertNames}>
          <div className={styles.outExpertName}>{outOfOfficePeriod.expert.firstName}</div>
          <div className={styles.backupExpertName}>
            <TextWithIcon icon="check" iconFirst text={outOfOfficePeriod.backupExpert.firstName} />
          </div>
        </div>
      </div>
    ),
    [outOfOfficePeriod.backupExpert.firstName, outOfOfficePeriod.expert]
  );

  const coverageClass = useMemo(() => {
    if (outOfOfficePeriod.expert.coversAsia && !outOfOfficePeriod.expert.coversEurope) {
      return styles.coversAsiaOoop;
    } else if (outOfOfficePeriod.expert.coversEurope && !outOfOfficePeriod.expert.coversAsia) {
      return styles.coversEuropeOoop;
    }
    return styles.coversMixOoop;
  }, [outOfOfficePeriod.expert.coversAsia, outOfOfficePeriod.expert.coversEurope]);

  if (phase === DayOoopPhase.CONTINUE) {
    return (
      <div
        className={`${styles.ooop} ${styles.ooopContinue} ${coverageClass}`}
        onClick={() => onClick(outOfOfficePeriod)}
      >
        <div className={`${styles.ooopStripe} ${styles.ooopContinueStripe}`} />
      </div>
    );
  }

  if (phase === DayOoopPhase.END) {
    return (
      <div className={`${styles.ooop} ${styles.ooopEnd} ${coverageClass}`} onClick={() => onClick(outOfOfficePeriod)}>
        <div className={`${styles.ooopStripe} ${styles.ooopEndStripe}`} />
      </div>
    );
  }

  if (phase === DayOoopPhase.START) {
    return (
      <div className={`${styles.ooop} ${styles.ooopStart} ${coverageClass}`} onClick={() => onClick(outOfOfficePeriod)}>
        {renderExpertData()}
        <div className={`${styles.ooopStripe} ${styles.ooopStartStripe}`} />
      </div>
    );
  }

  if (phase === DayOoopPhase.START_END) {
    return (
      <div
        className={`${styles.ooop} ${styles.ooopStartEnd} ${coverageClass}`}
        onClick={() => onClick(outOfOfficePeriod)}
      >
        {renderExpertData()}
        <div className={`${styles.ooopStripe} ${styles.ooopStartEndStripe}`} />
      </div>
    );
  }

  if (phase === DayOoopPhase.COMPLETE) {
    return <div className={styles.ooop}></div>;
  }

  return null;
};

const OoopDetail = ({ onCancel, onEdit, outOfOfficePeriod }: OoopDetailProps) => {
  const myAdmin = useMyAdmin();
  const myExpert = useMyExpert();

  const destroyOpener = useOpener();

  const [destroyOutOfOfficePeriod, { client }] = useMutation<
    OutOfOfficePeriodDestroyMutation,
    OutOfOfficePeriodDestroyMutationVariables
  >(OutOfOfficePeriodDestroyDocument, {
    update: () => {
      client.resetStore();
    }
  });

  const handleDelete = useCallback(() => {
    destroyOpener.close();
    destroyOutOfOfficePeriod({ variables: { id: outOfOfficePeriod.id } }).then(() => onCancel());
  }, [destroyOpener, destroyOutOfOfficePeriod, onCancel, outOfOfficePeriod.id]);

  const canEdit = useMemo(
    () =>
      (myAdmin || myExpert?.expertGlobalRole === ExpertGlobalRoleEnum.MANAGER) &&
      outOfOfficePeriod.endTime > new Date(),
    [myAdmin, myExpert?.expertGlobalRole, outOfOfficePeriod.endTime]
  );

  return (
    <IonGrid className={`ion-no-margin ion-no-padding ${styles.ooopDetail}`}>
      <IonRow className="ion-align-items-center">
        <IonCol size="auto">
          <ExpertAvatar className={styles.outExpertPhoto} expert={outOfOfficePeriod.expert} size={85} />
        </IonCol>
        <IonCol className={styles.outExpertInfo}>
          <div>{outOfOfficePeriod.expert.firstName}</div>
          <div>
            {outOfOfficePeriod.expert.coversAsia && (
              <div className={styles.coversAsiaBadge}>
                <FormattedMessage id="dictionary.asia" />
              </div>
            )}
            {outOfOfficePeriod.expert.coversEurope && (
              <div className={styles.coversEuropeBadge}>
                <FormattedMessage id="dictionary.europe" />
              </div>
            )}
          </div>
        </IonCol>
      </IonRow>
      <IonRow>
        <IonCol className={styles.timeInfo}>
          <div className={styles.label}>
            <FormattedMessage id="pages.expert.outOfOffice.startTime" />
          </div>
          <div>
            <DateAndTimeStamp
              timezone={outOfOfficePeriod.expert.timezone ?? undefined}
              value={outOfOfficePeriod.startTime}
            />
          </div>
        </IonCol>
      </IonRow>
      <IonRow>
        <IonCol className={styles.timeInfo}>
          <div className={styles.label}>
            <FormattedMessage id="pages.expert.outOfOffice.endTime" />
          </div>
          <div>
            <DateAndTimeStamp
              timezone={outOfOfficePeriod.expert.timezone ?? undefined}
              value={outOfOfficePeriod.endTime}
            />
          </div>
        </IonCol>
      </IonRow>
      <IonRow>
        <IonCol className={styles.backupInfo}>
          <div className={styles.label}>
            <FormattedMessage id="pages.expert.outOfOffice.backupExpert" />
          </div>
          <IonGrid className="ion-no-margin ion-no-padding">
            <IonRow className="ion-align-items-center">
              <IonCol size="auto">
                <ExpertAvatar className={styles.backupExpertPhoto} expert={outOfOfficePeriod.backupExpert} size={50} />
              </IonCol>
              <IonCol className={styles.outExpertInfo}>
                <div>{outOfOfficePeriod.backupExpert.firstName}</div>
                <div>
                  {outOfOfficePeriod.backupExpert.coversAsia && (
                    <div className={styles.coversAsiaBadge}>
                      <FormattedMessage id="dictionary.asia" />
                    </div>
                  )}
                  {outOfOfficePeriod.backupExpert.coversEurope && (
                    <div className={styles.coversEuropeBadge}>
                      <FormattedMessage id="dictionary.europe" />
                    </div>
                  )}
                </div>
              </IonCol>
            </IonRow>
          </IonGrid>
        </IonCol>
      </IonRow>
      {destroyOpener.isOpen ? (
        <>
          <IonRow className={styles.destroyMessageRow}>
            <IonCol>
              <div className={styles.destroyMessage}>
                <FormattedMessage id="pages.expert.outOfOffice.reallyDelete" />
              </div>
            </IonCol>
          </IonRow>
          <IonRow className={`${styles.destroyButtonsRow} ion-justify-content-center`}>
            <IonCol size="auto">
              <SecondaryButton onClick={destroyOpener.close} textKey="dictionary.cancel" />
            </IonCol>
            <IonCol size="auto">
              <PrimaryButton onClick={handleDelete} textKey="dictionary.confirm" />
            </IonCol>
          </IonRow>
        </>
      ) : (
        <>
          {canEdit && (
            <IonRow className={styles.buttonsRow}>
              <IonCol size="auto">
                <PrimaryButton onClick={() => onEdit(outOfOfficePeriod)} textKey="pages.expert.outOfOffice.editEvent" />
              </IonCol>
              <IonCol size="auto">
                <PrimaryButton onClick={destroyOpener.open} textKey="pages.expert.outOfOffice.deleteEvent" />
              </IonCol>
              <IonCol size="auto">
                <SecondaryButton onClick={onCancel} textKey="dictionary.close" />
              </IonCol>
            </IonRow>
          )}
        </>
      )}
    </IonGrid>
  );
};

const OutOfOfficeCalendar = ({ onEdit }: Props) => {
  const intl = useIntl();
  const me = useMe();

  const [detailOoop, setDetailOoop] = useState<OutOfOfficePeriod>();
  const detailOpener = useOpener();

  const startOfThisMonth = useMemo(() => {
    const currentDate = new Date();
    const startOfMyMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1, 0, 0, 0);
    return startOfMyMonth;
  }, []);

  const [currentStartOfMonth, setCurrentStartOfMonth] = useState<Date>(startOfThisMonth);

  const currentEndOfMonth = useMemo(
    () => new Date(currentStartOfMonth.getFullYear(), currentStartOfMonth.getMonth() + 1, 0, 23, 59, 59),
    [currentStartOfMonth]
  );

  const getOutOfOfficePeriodsQuery = useQuery<GetOutOfOfficePeriodsQuery, GetOutOfOfficePeriodsQueryVariables>(
    GetOutOfOfficePeriodsDocument,
    { variables: { endDate: currentEndOfMonth.toISOString(), startDate: currentStartOfMonth.toISOString() } }
  );

  const outOfOfficePeriods = useNodesFromConnection(getOutOfOfficePeriodsQuery.data?.outOfOfficePeriods);

  const getGrayceHolidaysQuery = useQuery<GetGrayceHolidaysQuery, GetGrayceHolidaysQueryVariables>(
    GetGrayceHolidaysDocument
  );

  const advanceDateByDays = useCallback((date: Date, days: number) => {
    const copy = new Date(date.getTime());
    copy.setDate(copy.getDate() + days);
    return copy;
  }, []);

  const isDateHoliday = useCallback(
    (date: Date) => {
      const isHoliday =
        getGrayceHolidaysQuery.data?.grayceHolidays.some(
          (holiday: Date) =>
            date.getFullYear() === holiday.getFullYear() &&
            date.getMonth() === holiday.getMonth() &&
            date.getDate() === holiday.getDate()
        ) || false;
      return isHoliday;
    },
    [getGrayceHolidaysQuery.data?.grayceHolidays]
  );

  const isDateHolidayOrWeekend = useCallback(
    (date: Date) => isDateHoliday(date) || date.getDay() === 0 || date.getDay() === 6,
    [isDateHoliday]
  );

  const moveToBeginningOfNextDay = useCallback(
    (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1, 0, 0, 0),
    []
  );

  const moveToEndOfPreviousDay = useCallback(
    (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate() - 1, 23, 59, 59),
    []
  );

  const dateInTimezone = useCallback((date: Date, timezone: string) => {
    const utcDate = new Date(
      Date.UTC(
        date.getUTCFullYear(),
        date.getUTCMonth(),
        date.getUTCDate(),
        date.getUTCHours(),
        date.getUTCMinutes(),
        date.getUTCSeconds()
      )
    );

    return utcToZonedTime(utcDate, timezone);
  }, []);

  const usableOoopTimes = useCallback(
    (ooop: OutOfOfficePeriod) => {
      let startTime = ooop.startTime;
      let endTime = ooop.endTime;

      if (ooop.expert.timezone && me?.timezone) {
        startTime = dateInTimezone(startTime, ooop.expert.timezone);
        endTime = dateInTimezone(endTime, ooop.expert.timezone);
      }

      if (startTime.getHours() >= 16) {
        startTime = moveToBeginningOfNextDay(startTime);
      }
      while (isDateHolidayOrWeekend(startTime)) {
        startTime = moveToBeginningOfNextDay(startTime);
      }

      if (endTime.getHours() < 8 || (endTime.getHours() === 8 && endTime.getMinutes() === 0)) {
        endTime = moveToEndOfPreviousDay(endTime);
      }
      while (isDateHolidayOrWeekend(endTime)) {
        endTime = moveToEndOfPreviousDay(endTime);
      }

      if (startTime > endTime) {
        return [ooop.startTime, ooop.endTime];
      }

      return [startTime, endTime];
    },
    [dateInTimezone, isDateHolidayOrWeekend, me?.timezone, moveToBeginningOfNextDay, moveToEndOfPreviousDay]
  );

  const findOoopsForDay = useCallback(
    (dayStartTime: Date, ooopsInWeek: Set<OutOfOfficePeriod>) => {
      const result: DayOoopType[] = [];
      const dayEndTime = new Date(
        dayStartTime.getFullYear(),
        dayStartTime.getMonth(),
        dayStartTime.getDate(),
        23,
        59,
        59
      );

      const tomorrow = new Date(dayStartTime.getTime() + oneDayInMs);
      const yesterday = new Date(dayStartTime.getTime() - oneDayInMs);

      if (isDateHoliday(dayStartTime)) {
        return result;
      }

      outOfOfficePeriods.forEach(ooop => {
        const [ooopStartTime, ooopEndTime] = usableOoopTimes(ooop);

        if (ooopStartTime >= dayStartTime && ooopStartTime < dayEndTime) {
          if (ooopEndTime <= dayEndTime) {
            result.push({ outOfOfficePeriod: ooop, phase: DayOoopPhase.START_END });
          } else {
            if (isDateHolidayOrWeekend(tomorrow)) {
              result.push({ outOfOfficePeriod: ooop, phase: DayOoopPhase.START_END });
            } else {
              result.push({ outOfOfficePeriod: ooop, phase: DayOoopPhase.START });
            }
          }
        } else if (ooopEndTime >= dayStartTime && ooopEndTime <= dayEndTime) {
          if (isDateHolidayOrWeekend(yesterday)) {
            result.push({ outOfOfficePeriod: ooop, phase: DayOoopPhase.START_END });
          } else {
            if (ooopStartTime >= dayStartTime) {
              result.push({ outOfOfficePeriod: ooop, phase: DayOoopPhase.START_END });
            } else {
              result.push({ outOfOfficePeriod: ooop, phase: DayOoopPhase.END });
            }
          }
        } else if (ooopStartTime < dayStartTime && ooopEndTime > dayEndTime) {
          if (dayStartTime.getDay() === 1) {
            result.push({ outOfOfficePeriod: ooop, phase: DayOoopPhase.START });
          } else {
            if (isDateHolidayOrWeekend(tomorrow)) {
              result.push({ outOfOfficePeriod: ooop, phase: DayOoopPhase.END });
            } else if (isDateHoliday(yesterday)) {
              result.push({ outOfOfficePeriod: ooop, phase: DayOoopPhase.START });
            } else {
              if (!isDateHoliday(dayStartTime)) {
                result.push({ outOfOfficePeriod: ooop, phase: DayOoopPhase.CONTINUE });
              }
            }
          }
        }
      });

      ooopsInWeek.forEach(ooop => {
        if (!result.some(item => item.outOfOfficePeriod.id === ooop.id)) {
          result.push({ outOfOfficePeriod: ooop, phase: DayOoopPhase.COMPLETE });
        }
      });

      return sortByTimestamp(result, ooop => ooop.outOfOfficePeriod.startTime).reverse();
    },
    [isDateHoliday, isDateHolidayOrWeekend, outOfOfficePeriods, usableOoopTimes]
  );

  const calendarWeeks = useMemo(() => {
    let thisDate = currentStartOfMonth;
    const currentMonth = thisDate.getMonth();
    const weeks: Week[] = [];
    let weekId = 0;
    let thisWeek: Week = { days: [], id: weekId };
    let ooopsInWeek = new Set<OutOfOfficePeriod>();

    if (thisDate.getDay() > 1 && thisDate.getDay() < 6) {
      while (thisDate.getDay() !== 1) {
        thisDate = advanceDateByDays(thisDate, -1);
      }
    }

    while (thisDate.getMonth() === currentMonth || thisDate.getMonth() === currentMonth - 1) {
      if (thisDate.getDay() > 0 && thisDate.getDay() < 6) {
        const ooopsForDay = findOoopsForDay(thisDate, ooopsInWeek);
        ooopsForDay.forEach(ooop => ooopsInWeek.add(ooop.outOfOfficePeriod));
        thisWeek.days.push({
          date: thisDate,
          holiday: isDateHoliday(thisDate),
          id: thisDate.getDay(),
          ooops: ooopsForDay
        });
      } else if (thisDate.getDay() === 6) {
        weeks.push(thisWeek);
        weekId += 1;
        thisWeek = { days: [], id: weekId };
        ooopsInWeek = new Set<OutOfOfficePeriod>();
      }

      thisDate = advanceDateByDays(thisDate, 1);
    }

    if (thisWeek.days.length > 0) {
      if (thisDate.getDay() > 0 && thisDate.getDay() < 6) {
        while (thisDate.getDay() < 6) {
          const ooopsForDay = findOoopsForDay(thisDate, ooopsInWeek);
          ooopsForDay.forEach(ooop => ooopsInWeek.add(ooop.outOfOfficePeriod));
          thisWeek.days.push({
            date: thisDate,
            holiday: isDateHoliday(thisDate),
            id: thisDate.getDay(),
            ooops: ooopsForDay
          });
          thisDate = advanceDateByDays(thisDate, 1);
        }
      }

      weeks.push(thisWeek);
    }

    return weeks;
  }, [advanceDateByDays, currentStartOfMonth, findOoopsForDay, isDateHoliday]);

  const advanceMonth = useCallback(
    (count: number) => {
      const startOfMonth = new Date(
        currentStartOfMonth.getFullYear(),
        currentStartOfMonth.getMonth() + count,
        1,
        0,
        0,
        0
      );

      setCurrentStartOfMonth(startOfMonth);
    },
    [currentStartOfMonth]
  );

  const handlePreviousMonth = useCallback(() => {
    advanceMonth(-1);
  }, [advanceMonth]);

  const handleNextMonth = useCallback(() => {
    advanceMonth(1);
  }, [advanceMonth]);

  const handleEditOoop = useCallback(
    (outOfOfficePeriod: OutOfOfficePeriod) => {
      detailOpener.close();
      onEdit(outOfOfficePeriod);
    },
    [detailOpener, onEdit]
  );

  const showOoopDetail = useCallback(
    (ooop: OutOfOfficePeriod) => {
      setDetailOoop(ooop);
      detailOpener.open();
    },
    [detailOpener]
  );

  return (
    <HandleQuery query={getOutOfOfficePeriodsQuery}>
      {_data => (
        <>
          <IonGrid className={`${styles.calendarGrid} ion-no-padding`}>
            <IonRow className="ion-no-padding">
              <IonCol />
              <IonCol />
              <IonCol>
                <IonGrid className={styles.monthSelectorGrid}>
                  <IonRow>
                    <IonCol size="2">
                      <ActionIconButton
                        ariaLabelKey="accessibility.ariaLabels.previous"
                        icon="arrowLeft"
                        onClick={handlePreviousMonth}
                      />
                    </IonCol>
                    <IonCol>
                      <DateStamp absoluteDate value={currentStartOfMonth} />
                    </IonCol>
                    <IonCol size="2">
                      <ActionIconButton
                        ariaLabelKey="accessibility.ariaLabels.next"
                        icon="arrowRight"
                        onClick={handleNextMonth}
                      />
                    </IonCol>
                  </IonRow>
                </IonGrid>
              </IonCol>
              <IonCol />
              <IonCol />
            </IonRow>
            <IonRow className="ion-no-padding">
              {getWeekdays(intl.locale).map(weekday => (
                <IonCol className={styles.dayName} key={weekday}>
                  {weekday.toUpperCase()}
                </IonCol>
              ))}
            </IonRow>
            {calendarWeeks.map(week => (
              <IonRow className="ion-no-padding" key={week.id}>
                {week.days.map(day => (
                  <IonCol className={styles.day} key={day.id}>
                    <div className={`${day.date < new Date() ? styles.pastDateNumber : ""} ${styles.dateNumber}`}>
                      <FormattedDate day="numeric" timeZone="utc" value={day.date} />
                    </div>
                    {day.ooops.map(dayOoop => (
                      <DayOoop
                        key={dayOoop.outOfOfficePeriod.id}
                        onClick={showOoopDetail}
                        outOfOfficePeriod={dayOoop.outOfOfficePeriod}
                        phase={dayOoop.phase}
                      />
                    ))}
                    {day.holiday && (
                      <div className={styles.holiday}>
                        <FormattedMessage id="pages.expert.outOfOffice.grayceHoliday" />
                      </div>
                    )}
                  </IonCol>
                ))}
              </IonRow>
            ))}
          </IonGrid>
          <Modal opener={detailOpener} title={intl.formatMessage({ id: "pages.expert.outOfOffice.outOfOfficeEvent" })}>
            {() => (
              <OoopDetail
                onCancel={detailOpener.close}
                onEdit={handleEditOoop}
                outOfOfficePeriod={detailOoop as OutOfOfficePeriod}
              />
            )}
          </Modal>
        </>
      )}
    </HandleQuery>
  );
};

export default OutOfOfficeCalendar;
